/**
 * Sales Team Report parameter state — pure, so the validation rules are
 * testable without rendering the modal.
 *
 * Two windows:
 *   RECENT — one report date; the funnel shows the last 5 WORKING days ending
 *     there (the per-location working-day schedule decides which, which is why
 *     the client sends only the date and never a computed 5-day range).
 *   RANGE  — startDate + endDate; every calendar day, with weekly trend columns.
 *
 * RECENT is the default so the report opens showing exactly what it always has.
 */

import {
  FUNNEL_HISTORY_START,
  FUNNEL_MAX_RANGE_DAYS,
} from '../../../types/salesTeamFunnel';
import {
  firstOfMonth,
  formatLongDate,
  inclusiveDayCount,
  todayLocal,
} from '../../../utils/localDate';

// Vehicle-selling rooftops (Loc 12 & 44 are Body Shop and excluded).
export const ELIGIBLE_LOCATIONS = [
  { id: 1, name: "Darling's Ford VW Audi" },
  { id: 2, name: "Darling's Honda Nissan Volvo" },
  { id: 3, name: "Darling's Auto Mall" },
  { id: 4, name: "Darling's Chrysler Dodge Hyundai" },
  { id: 5, name: "Darlings Chevrolet" },
  { id: 10, name: "Brunswick Ford" },
  { id: 11, name: "Darlings CDJR Newport" },
  { id: 14, name: "Darling's Kia" },
];

export type FunnelMode = 'recent' | 'range';

export interface FunnelReportParams {
  mode: FunnelMode;
  /** Recent mode's single report date. */
  reportDate: string;
  /** Range mode bounds. Kept across a mode flip so switching is lossless. */
  startDate: string;
  endDate: string;
  locationIds: Set<number>;
  /** True once range mode has seeded its dates, so later edits are preserved. */
  rangeSeeded: boolean;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** All rooftops selected, recent mode, today — the report's opening state. */
export function defaultFunnelParams(): FunnelReportParams {
  const today = todayLocal();
  return {
    mode: 'recent',
    reportDate: today,
    startDate: '',
    endDate: today,
    locationIds: new Set(ELIGIBLE_LOCATIONS.map((l) => l.id)),
    rangeSeeded: false,
  };
}

/** The default range start for a given end date: month-to-date, never pre-history. */
export function defaultRangeStart(endDate: string): string {
  const first = firstOfMonth(endDate);
  return first < FUNNEL_HISTORY_START ? FUNNEL_HISTORY_START : first;
}

/**
 * Switch to range mode, seeding month-to-date on FIRST entry only. Re-entering
 * later keeps whatever range the operator last chose.
 */
export function enterRangeMode(p: FunnelReportParams): FunnelReportParams {
  if (p.rangeSeeded) return { ...p, mode: 'range' };
  const endDate = p.endDate || todayLocal();
  return {
    ...p,
    mode: 'range',
    endDate,
    startDate: defaultRangeStart(endDate),
    rangeSeeded: true,
  };
}

/**
 * Change the range end, re-clamping the start when the edit would invert the
 * range (a date input lets you type any year).
 */
export function setRangeEnd(p: FunnelReportParams, endDate: string): FunnelReportParams {
  const next = { ...p, endDate };
  if (ISO.test(endDate) && ISO.test(p.startDate) && p.startDate > endDate) {
    next.startDate = defaultRangeStart(endDate);
  }
  return next;
}

/** Selected location ids, ascending. */
export function selectedIds(p: FunnelReportParams): number[] {
  return ELIGIBLE_LOCATIONS.filter((l) => p.locationIds.has(l.id)).map((l) => l.id);
}

/**
 * Whether the report can run, plus a user-facing reason when it cannot.
 *
 * `message: null` with `canRun: false` means "input still incomplete" (the
 * operator is mid-typing in a date field) — the Run button is disabled but no
 * error is shown, so nobody gets yelled at while typing.
 *
 * This duplicates the server's rules on purpose: the picker's min/max
 * attributes are advisory (Firefox honors `min` only weakly on a typed date),
 * and the backend re-validates regardless — a direct API call must not get
 * through on the client's good behaviour alone.
 *
 * One deliberate asymmetry: "today" here is the BROWSER's local date, while the
 * server bounds on the dealership's Eastern date. They agree for everyone
 * working in Maine. A user in a timezone ahead of Eastern could pick a date the
 * picker allows and the server rejects, which surfaces as a clear 400 naming
 * the dealership's date rather than as bad data — Eastern is the correct
 * authority for a dealership calendar, so that is the right way round.
 */
export function validateFunnelParams(
  p: FunnelReportParams,
): { canRun: boolean; message: string | null } {
  const today = todayLocal();

  if (selectedIds(p).length === 0) {
    return { canRun: false, message: 'Select at least one location.' };
  }

  if (p.mode === 'recent') {
    if (!ISO.test(p.reportDate)) return { canRun: false, message: null };
    if (p.reportDate > today) {
      return { canRun: false, message: 'Report date cannot be in the future.' };
    }
    return { canRun: true, message: null };
  }

  if (!ISO.test(p.startDate) || !ISO.test(p.endDate)) {
    return { canRun: false, message: null };
  }
  if (p.startDate > p.endDate) {
    return { canRun: false, message: 'End date must be on or after start date.' };
  }
  if (p.startDate < FUNNEL_HISTORY_START) {
    return {
      canRun: false,
      message: `Lead and visit history begins ${formatLongDate(FUNNEL_HISTORY_START)}.`,
    };
  }
  if (p.endDate > today) {
    return { canRun: false, message: 'End date cannot be in the future.' };
  }

  const days = inclusiveDayCount(p.startDate, p.endDate);
  if (days > FUNNEL_MAX_RANGE_DAYS) {
    return {
      canRun: false,
      message: `Date range is ${days} days — maximum is ${FUNNEL_MAX_RANGE_DAYS}. `
        + 'Shorten the range to run.',
    };
  }

  return { canRun: true, message: null };
}
